import React, { useState, useEffect, useRef } from 'react';
import { 
    Box, 
    VStack, 
    HStack, 
    Button, 
    Text, 
    Input, 
    Select, 
    IconButton, 
    Switch, 
    FormControl, 
    FormLabel, 
    SimpleGrid,
    Badge,
    useToast,
    Collapse,
    Divider,
    Icon,
    Tooltip
} from '@chakra-ui/react';
import { AddIcon, DeleteIcon } from '@chakra-ui/icons';
import { MdSchedule, MdTimelapse, MdAccessTime } from 'react-icons/md';
import { getTranslations } from '../translations';

const RestTimeRules = ({ rounds, defaultRestTime, restTimeRules, setRestTimeRules, isDarkMode, isActive }) => {
    const t = getTranslations();
    const toast = useToast();
    const [isEnabled, setIsEnabled] = useState(restTimeRules && restTimeRules.length > 0);
    const [isTooltipOpen, setIsTooltipOpen] = useState(false);
    const isTogglingRef = useRef(false);

    // Inizializza restTimeRules se non esiste
    useEffect(() => {
        if (!restTimeRules) {
            setRestTimeRules([]);
        }
    }, [restTimeRules, setRestTimeRules]);

    // Sincronizza isEnabled quando restTimeRules cambia dall'esterno (es. caricamento profilo)
    // Ma ignora i cambiamenti quando stiamo facendo toggle manualmente
    useEffect(() => {
        if (isTogglingRef.current) {
            return;
        }
        const hasRules = restTimeRules && Array.isArray(restTimeRules) && restTimeRules.length > 0;
        setIsEnabled(hasRules);
    }, [restTimeRules]);

    // Calcola il tempo di riposo per un round specifico
    const getRestTimeForRound = (round) => {
        if (!isEnabled || !restTimeRules || restTimeRules.length === 0) {
            return defaultRestTime;
        }

        // Valuta le regole in ordine inverso (le più specifiche per ultime)
        const reversedRules = [...restTimeRules].reverse();
        for (let rule of reversedRules) {
            if (rule.type === 'after' && round >= rule.round) {
                return rule.restTime;
            }
            if (rule.type === 'every' && round % rule.interval === 0) {
                return rule.restTime;
            }
            if (rule.type === 'specific' && rule.rounds.includes(round)) {
                return rule.restTime;
            }
        }
        return defaultRestTime;
    };

    // Genera il riepilogo dei tempi per tutti i round
    const generateSummary = () => {
        const summary = [];
        for (let i = 1; i <= rounds; i++) {
            summary.push({
                round: i,
                restTime: getRestTimeForRound(i)
            });
        }
        return summary;
    };

    const addRule = () => {
        const newRule = {
            type: 'after',
            round: 1,
            interval: 1,
            rounds: [],
            restTime: defaultRestTime
        };
        setRestTimeRules([...restTimeRules, newRule]);
    };

    const removeRule = (index) => {
        const newRules = restTimeRules.filter((_, i) => i !== index);
        setRestTimeRules(newRules);
        if (newRules.length === 0) {
            setIsEnabled(false);
        }
    };

    const updateRule = (index, field, value) => {
        const newRules = [...restTimeRules];
        if (field === 'rounds' && typeof value === 'string') {
            // Parse comma-separated round numbers
            const roundsArray = value.split(',').map(r => parseInt(r.trim(), 10)).filter(r => !isNaN(r) && r > 0 && r <= rounds);
            newRules[index].rounds = roundsArray;
        } else {
            newRules[index][field] = value;
        }
        setRestTimeRules(newRules);
    };

    const handleToggle = (enabled) => {
        // Imposta il flag prima di fare qualsiasi modifica
        isTogglingRef.current = true;
        
        if (!enabled) {
            // Disabilita immediatamente
            setIsEnabled(false);
            // Poi svuota le regole
            setRestTimeRules([]);
        } else {
            // Abilita immediatamente
            setIsEnabled(true);
            // Poi aggiungi regola se necessario
            if (!restTimeRules || restTimeRules.length === 0) {
                addRule();
            }
        }
        
        // Reset del flag dopo che React ha processato gli aggiornamenti
        setTimeout(() => {
            isTogglingRef.current = false;
        }, 200);
    };

    const summary = generateSummary();

    return (
        <Box w="100%" mt={4}>
            {!isActive && (
                <>
                    <HStack justify="space-between" mb={3}>
                        <FormControl display="flex" alignItems="center">
                            <Icon 
                                as={MdSchedule} 
                                boxSize="1.5rem" 
                                color={isDarkMode ? (isEnabled ? "orange.300" : "gray.500") : (isEnabled ? "orange.600" : "gray.400")} 
                                mr={2}
                            />
                            <Tooltip 
                                label={t.restTimeVariableDesc || 'Configura tempi di riposo diversi per round specifici'} 
                                hasArrow
                                isOpen={isTooltipOpen}
                                onOpen={() => setIsTooltipOpen(true)}
                                onClose={() => setIsTooltipOpen(false)}
                                openDelay={300}
                                closeDelay={500}
                                closeOnClick={false}
                                closeOnMouseDown={true}
                                closeOnEsc={true}
                            >
                                <Box 
                                    display="inline-block"
                                    onMouseEnter={() => setIsTooltipOpen(true)}
                                    onMouseLeave={() => setTimeout(() => setIsTooltipOpen(false), 500)}
                                >
                                    <Switch
                                        id="rest-time-variable"
                                        isChecked={isEnabled}
                                        onChange={(e) => {
                                            handleToggle(e.target.checked);
                                            setIsTooltipOpen(false);
                                        }}
                                        colorScheme="orange"
                                        size="md"
                                    />
                                </Box>
                            </Tooltip>
                        </FormControl>
                    </HStack>

                    <Collapse in={isEnabled} animateOpacity>
                        <Box
                            p={4}
                            bg={isDarkMode ? "gray.800" : "gray.50"}
                            borderRadius="md"
                            borderWidth="1px"
                            borderColor={isDarkMode ? "gray.700" : "gray.200"}
                        >
                            <Text fontSize="sm" color={isDarkMode ? "gray.300" : "gray.600"} mb={4}>
                                {t.restTimeVariableDesc || 'Configura tempi di riposo diversi per round specifici'}
                            </Text>

                            <VStack spacing={3} align="stretch">
                                {restTimeRules.map((rule, index) => (
                                    <Box
                                        key={index}
                                        p={3}
                                        bg={isDarkMode ? "gray.700" : "white"}
                                        borderRadius="md"
                                        borderWidth="1px"
                                        borderColor={isDarkMode ? "gray.600" : "gray.300"}
                                    >
                                        <HStack justify="space-between" mb={2}>
                                            <Text fontSize="sm" fontWeight="bold" color={isDarkMode ? "gray.200" : "gray.800"}>
                                                {t.rule || 'Regola'} {index + 1}
                                            </Text>
                                            <IconButton
                                                icon={<DeleteIcon />}
                                                size="sm"
                                                colorScheme="red"
                                                variant="ghost"
                                                onClick={() => removeRule(index)}
                                                aria-label={t.removeRule || 'Rimuovi regola'}
                                            />
                                        </HStack>

                                        <SimpleGrid columns={2} spacing={2}>
                                            <FormControl>
                                                <FormLabel fontSize="xs" color={isDarkMode ? "gray.400" : "gray.600"}>
                                                    {t.ruleType || 'Tipo'}
                                                </FormLabel>
                                                <Select
                                                    size="sm"
                                                    value={rule.type}
                                                    onChange={(e) => updateRule(index, 'type', e.target.value)}
                                                    bg={isDarkMode ? "gray.600" : "white"}
                                                    color={isDarkMode ? "white" : "gray.800"}
                                                >
                                                    <option value="after">{t.ruleTypeAfter || 'Dopo round X'}</option>
                                                    <option value="every">{t.ruleTypeEvery || 'Ogni N round'}</option>
                                                    <option value="specific">{t.ruleTypeSpecific || 'Round specifici'}</option>
                                                </Select>
                                            </FormControl>

                                            {rule.type === 'after' && (
                                                <FormControl>
                                                    <FormLabel fontSize="xs" color={isDarkMode ? "gray.400" : "gray.600"}>
                                                        {t.ruleAfterRound || 'Dopo round'}
                                                    </FormLabel>
                                                    <Input
                                                        type="number"
                                                        size="sm"
                                                        value={rule.round || ''}
                                                        onChange={(e) => updateRule(index, 'round', Math.max(1, Math.min(rounds, parseInt(e.target.value, 10) || 1)))}
                                                        min={1}
                                                        max={rounds}
                                                        bg={isDarkMode ? "gray.600" : "white"}
                                                        color={isDarkMode ? "white" : "gray.800"}
                                                    />
                                                </FormControl>
                                            )}

                                            {rule.type === 'every' && (
                                                <FormControl>
                                                    <FormLabel fontSize="xs" color={isDarkMode ? "gray.400" : "gray.600"}>
                                                        {t.ruleEveryInterval || 'Ogni N round'}
                                                    </FormLabel>
                                                    <Input
                                                        type="number"
                                                        size="sm"
                                                        value={rule.interval || ''}
                                                        onChange={(e) => updateRule(index, 'interval', Math.max(1, Math.min(rounds, parseInt(e.target.value, 10) || 1)))}
                                                        min={1}
                                                        max={rounds}
                                                        bg={isDarkMode ? "gray.600" : "white"}
                                                        color={isDarkMode ? "white" : "gray.800"}
                                                    />
                                                </FormControl>
                                            )}

                                            {rule.type === 'specific' && (
                                                <FormControl gridColumn="span 2">
                                                    <FormLabel fontSize="xs" color={isDarkMode ? "gray.400" : "gray.600"}>
                                                        {t.ruleSpecificRounds || 'Round (es: 1, 3, 5)'}
                                                    </FormLabel>
                                                    <Input
                                                        type="text"
                                                        size="sm"
                                                        value={rule.rounds ? rule.rounds.join(', ') : ''}
                                                        onChange={(e) => updateRule(index, 'rounds', e.target.value)}
                                                        placeholder="1, 3, 5"
                                                        bg={isDarkMode ? "gray.600" : "white"}
                                                        color={isDarkMode ? "white" : "gray.800"}
                                                    />
                                                </FormControl>
                                            )}

                                            <FormControl gridColumn="span 2">
                                                <FormLabel fontSize="xs" color={isDarkMode ? "gray.400" : "gray.600"}>
                                                    {t.ruleRestTime || 'Tempo riposo (s)'}
                                                </FormLabel>
                                                <Input
                                                    type="number"
                                                    size="sm"
                                                    value={rule.restTime || ''}
                                                    onChange={(e) => updateRule(index, 'restTime', Math.max(0, parseInt(e.target.value, 10) || 0))}
                                                    min={0}
                                                    max={9999}
                                                    bg={isDarkMode ? "gray.600" : "white"}
                                                    color={isDarkMode ? "white" : "gray.800"}
                                                />
                                            </FormControl>
                                        </SimpleGrid>
                                    </Box>
                                ))}

                                <Button
                                    leftIcon={<AddIcon />}
                                    size="sm"
                                    colorScheme="orange"
                                    variant="outline"
                                    onClick={addRule}
                                    isDisabled={!isEnabled}
                                >
                                    {t.addRule || 'Aggiungi Regola'}
                                </Button>
                            </VStack>

                            {summary.length > 0 && (
                                <>
                                    <Divider my={4} />
                                    <Box>
                                        <Text fontSize="sm" fontWeight="bold" mb={2} color={isDarkMode ? "gray.200" : "gray.800"}>
                                            {t.restTimeSummary || 'Riepilogo Tempi di Riposo'}
                                        </Text>
                                        <SimpleGrid columns={Math.min(8, rounds)} spacing={2}>
                                            {summary.map((item) => (
                                                <Box
                                                    key={item.round}
                                                    p={2}
                                                    bg={item.restTime !== defaultRestTime ? (isDarkMode ? "orange.900" : "orange.100") : (isDarkMode ? "gray.700" : "gray.200")}
                                                    borderRadius="md"
                                                    textAlign="center"
                                                    borderWidth={item.restTime !== defaultRestTime ? "2px" : "1px"}
                                                    borderColor={item.restTime !== defaultRestTime ? "orange.400" : (isDarkMode ? "gray.600" : "gray.300")}
                                                >
                                                    <Text fontSize="xs" color={isDarkMode ? "gray.400" : "gray.600"}>
                                                        R{item.round}
                                                    </Text>
                                                    <Text fontSize="sm" fontWeight="bold" color={isDarkMode ? "orange.300" : "orange.700"}>
                                                        {item.restTime}s
                                                    </Text>
                                                </Box>
                                            ))}
                                        </SimpleGrid>
                                    </Box>
                                </>
                            )}
                        </Box>
                    </Collapse>
                </>
            )}
        </Box>
    );
};

export default RestTimeRules;
